import React, { useState } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { FiX, FiSend } from "react-icons/fi";

/**
 * Helper to extract initials from user display name or email.
 */
const getInitials = (name) => {
  if (!name) return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
};

/**
 * Helper to format timestamp into Todoist style (e.g. "Today 14:30").
 */
const formatCommentTime = (createdAt) => {
  if (!createdAt) return "";
  let date;
  if (typeof createdAt.toDate === "function") {
    date = createdAt.toDate();
  } else if (createdAt.seconds) {
    date = new Date(createdAt.seconds * 1000);
  } else {
    date = new Date(createdAt);
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const pad = (num) => String(num).padStart(2, "0");
  const timeStr = `${pad(date.getHours())}:${pad(date.getMinutes())}`;

  if (targetDate.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  } else if (targetDate.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  } else {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} ${timeStr}`;
  }
};

/**
 * CommentThread Component
 * 
 * Props:
 * - comments: Array of comment objects
 * - user: Active user session
 * - onAddComment: (text: string) => Promise<void>
 */
export function CommentThread({ comments = [], user, onAddComment }) {
  const [isOpen, setIsOpen] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!commentText.trim() || submitting) return;

    setSubmitting(true);
    try {
      await onAddComment(commentText.trim());
      setCommentText("");
    } catch (error) {
      console.error("Failed to add comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-4">
      {/* Collapsible Section Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm font-bold text-gray-800 hover:text-gray-900 transition-colors focus:outline-none select-none border-none"
      >
        {isOpen ? (
          <FaChevronDown className="w-3.5 h-3.5 text-gray-500" />
        ) : (
          <FaChevronRight className="w-3.5 h-3.5 text-gray-500" />
        )}
        <span>Comments</span>
        <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-semibold">
          {comments.length}
        </span>
      </button>

      {/* Collapsible Content Area */}
      {isOpen && (
        <div className="space-y-4 transition-all duration-200">
          {/* Scrollable list of comments */}
          {comments.length > 0 ? (
            <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
              {comments.map((comment) => {
                const isSystem = comment.type === "state_change" || comment.type === "assignment_change" || comment.isSystem;

                if (isSystem) {
                  return (
                    <div
                      key={comment.id}
                      className="flex items-center gap-2 pl-11 text-xs text-gray-500 italic py-1 border-l-2 border-gray-100 select-none"
                    >
                      <span className="text-gray-400 select-none">⚙️</span>
                      <span>{comment.text}</span>
                      <span className="text-[10px] text-gray-400 not-italic font-medium">
                        ({formatCommentTime(comment.createdAt)})
                      </span>
                    </div>
                  );
                }

                return (
                  <div key={comment.id} className="flex items-start gap-3 text-sm">
                    {/* User Avatar Initials */}
                    <div className="w-8 h-8 rounded-full bg-emerald-800 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 select-none shadow-sm">
                      {getInitials(comment.authorName)}
                    </div>
                    {/* Comment Content Box */}
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-800">{comment.authorName}</span>
                        <span className="text-[10px] text-gray-400 font-semibold select-none">
                          {formatCommentTime(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-gray-700 leading-relaxed font-medium bg-gray-50 p-2.5 rounded-r-xl rounded-bl-xl border border-gray-100 whitespace-pre-wrap break-words">
                        {comment.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-400 text-xs font-semibold select-none">
              💬 No comments yet. Start the conversation below!
            </div>
          )}

          {/* New comment input area */}
          <div className="flex items-start gap-3 pt-3 border-t border-gray-100">
            {/* Current user initials avatar */}
            <div className="w-8 h-8 rounded-full bg-emerald-800 text-white flex items-center justify-center font-bold text-xs flex-shrink-0 select-none shadow-sm">
              {getInitials(user?.displayName || user?.email)}
            </div>

            {/* Input card */}
            <div className="flex-1 min-w-0">
              <div className="relative border border-gray-250 rounded-lg hover:border-gray-350 focus-within:border-gray-400 transition-all duration-150 bg-white">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Comment"
                  rows={2}
                  className="w-full p-2.5 pr-8 text-sm placeholder-gray-400 text-gray-800 border-0 focus:outline-none focus:ring-0 resize-y rounded-t-lg min-h-[55px] font-medium"
                />
                
                {/* Textarea Actions Footer */}
                <div className="flex items-center justify-end px-3 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg gap-2 select-none">
                  {/* Cancel Button */}
                  <button
                    type="button"
                    onClick={() => setCommentText("")}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                    title="Cancel"
                  >
                    <FiX className="w-4 h-4" />
                  </button>

                  {/* Send/Save Button */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!commentText.trim() || submitting}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg shadow-sm transition-all duration-150 focus:outline-none border-none ${
                      commentText.trim() && !submitting
                        ? "bg-red-500 hover:bg-red-600 text-white cursor-pointer active:scale-95"
                        : "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                    }`}
                    title="Comment"
                  >
                    <FiSend className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
